import { CommonModule } from '@angular/common';
import { Component, OnInit, signal, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { createMask, InputMaskModule } from '@ngneat/input-mask';
import { ArrayValidators } from 'src/app/shared/validators/array.validator';
import { AccessGroup, AccessGroupSearchEvent } from '../../access-groups/access-group.model';
import { PageEvent } from '@angular/material/paginator';
import { AccessGroupService } from 'src/app/shared/services/access-group.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatError, MatSelectModule } from '@angular/material/select';
import { TopLoaderService } from 'src/app/core/services/top-loader.service';
import { InviteIdTagUserRequest } from '../charging-user-create-modal/charging-user-create.model';
import { ChargingUserService } from 'src/app/shared/services/charging-user.service';
import { firstValueFrom } from 'rxjs';
import { SnackbarService } from 'ok-angular-shared/components';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { ChargingUser } from '../charging-user.model';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { PhoneNumberInputComponent } from 'src/app/shared/components/phone-number-input/phone-number-input.component';
import { CountryCodeSelectorComponent } from 'src/app/shared/components/country-code-selector/country-code-selector.component';
interface AppInviteIdTagUserModalModel {
  alias: FormControl<string>;
  phoneNumber: FormControl<string>;
  selectedAccessGroups: FormControl<SelectedAccessGroup[]>;
  phoneCountryCode: FormControl<number>;
}
interface SelectedAccessGroup {
  identifier: string;
  name: string;
}
@Component({
  selector: 'adm-app-invite-tag-user',
  templateUrl: './app-invite-IdTagUser.component.html',
  styleUrl: './app-invite-IdTagUser.component.scss',
  imports: [
    MatIconModule,
    FormsModule,
    MatInputModule,
    MatDialogModule,
    MatFormFieldModule,
    MatDatepickerModule,
    InputMaskModule,
    ReactiveFormsModule,
    MatButtonModule,
    CommonModule,
    MatSelectModule,
    CustomDropDownArrowDirective,
    PhoneNumberInputComponent,
    CountryCodeSelectorComponent,
    MatError,
  ],
})
export class AppInviteIdTagUserComponent implements OnInit {
  data = inject<{
    user: ChargingUser;
  }>(MAT_DIALOG_DATA);
  private accessGroupService = inject(AccessGroupService);
  private loadingService = inject(TopLoaderService);
  private chargingUserService = inject(ChargingUserService);
  dialogRef = inject<MatDialogRef<AppInviteIdTagUserComponent>>(MatDialogRef);
  private snackbarService = inject(SnackbarService);

  public accessGroups = signal<AccessGroup[]>([]);
  public accessGroups$ = toObservable(this.accessGroups);
  public userFormGroup: FormGroup<AppInviteIdTagUserModalModel> = new FormGroup<AppInviteIdTagUserModalModel>({
    alias: new FormControl<string>('', { nonNullable: true }),
    phoneNumber: new FormControl<string>('', { nonNullable: true }),
    phoneCountryCode: new FormControl<number>(45, { nonNullable: true }),
    selectedAccessGroups: new FormControl<SelectedAccessGroup[]>(
      { value: [], disabled: true },
      {
        validators: [ArrayValidators.minLength(1)],
        nonNullable: true,
      }
    ),
  });
  customerNumberInputMask = createMask({
    rightAlign: false,
    alias: 'numeric',
    placeholder: '',
    min: 0,
    max: 9999999999,
  });

  ngOnInit(): void {
    this.userFormGroup.controls.selectedAccessGroups.setValidators(ArrayValidators.minLength(1));
    this.userFormGroup.controls.selectedAccessGroups.updateValueAndValidity();
    this.getAccessGroups(this.data.user.organizationId);
  }

  private getAccessGroups(organizationId: number) {
    const searchEvent: AccessGroupSearchEvent = new AccessGroupSearchEvent(new PageEvent());
    searchEvent.organizationId = organizationId;
    this.accessGroupService.getAccessGroups(searchEvent).subscribe({
      next: result => {
        this.accessGroups.set(result.items);
        this.userFormGroup.controls.selectedAccessGroups.enable();
      },
    });
  }

  save() {
    this.loadingService.setLoading(true);
    const selectedAccessGroups = this.userFormGroup.controls.selectedAccessGroups.value;

    const model: InviteIdTagUserRequest = {
      alias: this.userFormGroup.controls.alias.value,
      phoneNumber: this.userFormGroup.controls.phoneNumber.value,
      chargingUserIdentifier: this.data.user.identifier,
      accessGroups: selectedAccessGroups,
      phoneCountryCode: this.userFormGroup.controls.phoneCountryCode.value,
    };

    this.chargingUserService.inviteIdTagUser(model).subscribe({
      next: async () => {
        const chargingUser = await firstValueFrom(this.chargingUserService.getChargingUser(this.data.user.id));
        this.dialogRef.close({ chargingUser: chargingUser });
        this.snackbarService.success(`Bruger oprettet`);
      },
      error: (httpError: HttpErrorResponse) => {
        if (httpError.status == HttpStatusCode.BadRequest) {
          switch (httpError.error?.code) {
            case 101100:
              this.snackbarService.error('Mobilnummeret angivet er ikke et gyldigt telefonnummer for den indtastede landekode.');
              break;
            case 101101:
              this.snackbarService.error('Mobilnummeret er af et ugyldigt format for den indtastede landekode.');
              break;
            default:
              this.snackbarService.error(httpError.error?.message);
              break;
          }
        } else {
          this.snackbarService.error(`Der er sket en ukendt fejl: (${httpError.error?.message})`);
        }
      },
    });
  }
}
